/**
 * Takeoff mode state for one drawing set: the catalog (live via `takeoff.changed`),
 * server quantities (recomputed on every relevant change, local or remote), the
 * active tool / item, and the mark actions. Marks go over the annotation socket
 * like any markup, so collaborators see them instantly.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { ApiClient } from '../api';
import { toast } from '../components/basics';
import type { SyncClient } from '../sync';
import {
  MARK_SIZE,
  TakeoffConflict,
  isMeasurement,
  markSequence,
  takeoffApi,
  targetOf,
  type CatalogKind,
  type QuantitiesDTO,
  type TakeoffAssemblyDTO,
  type TakeoffChange,
  type TakeoffItemDTO,
  type TakeoffLink,
  type TakeoffStateDTO,
} from '../takeoff';
import { newId, type AnnotationDTO, type PointDTO } from '../types';
import { useTakeoffEngines } from './useTakeoffEngines';

export type TakeoffTool = 'select' | 'hand' | 'count' | 'linear' | 'area' | 'calibrate' | 'zone' | 'unit' | 'mask' | 'autocount' | 'schedule';
export type TakeoffTarget = { kind: 'item' | 'assembly'; id: string };

export interface TakeoffUi {
  tool: TakeoffTool;
  target: TakeoffTarget | null;
  /** Runs for the next linear measurement (allowance is per run). */
  runs: number;
  /** Highlight marks of this target (totals panel row click). */
  highlight: string | null;
  /** Unit type a drawn unit region belongs to. */
  unitTypeId: string | null;
  /** Selected mark (select tool). */
  selectedMark: string | null;
}

const INITIAL_UI: TakeoffUi = { tool: 'select', target: null, runs: 1, highlight: null, unitTypeId: null, selectedMark: null };

function createUiStore() {
  let state = INITIAL_UI;
  const subs = new Set<() => void>();
  return {
    get: () => state,
    set(patch: Partial<TakeoffUi>) {
      state = { ...state, ...patch };
      subs.forEach((f) => f());
    },
    subscribe(cb: () => void) {
      subs.add(cb);
      return () => subs.delete(cb);
    },
  };
}

export type TakeoffSession = ReturnType<typeof useTakeoff>;

const LIST_FOR: Record<string, keyof TakeoffStateDTO> = {
  item: 'items',
  assembly: 'assemblies',
  area: 'areas',
  unit_type: 'unit_types',
  mask: 'masks',
};
const KIND_PATH: Record<string, CatalogKind> = { items: 'items', assemblies: 'assemblies', areas: 'areas', unit_types: 'unit-types', masks: 'masks' };

export function applyTakeoffChange(state: TakeoffStateDTO, change: TakeoffChange): TakeoffStateDTO {
  const next = { ...state, version: Math.max(state.version, change.version ?? state.version) };
  if (change.kind === 'takeoff') return { ...next, ...(change.data as Partial<TakeoffStateDTO>) };
  if (change.kind === 'sheet') {
    const row = change.data as unknown as TakeoffStateDTO['sheets'][number];
    const sheets = state.sheets.filter((s) => s.page_index !== row.page_index);
    return { ...next, sheets: [...sheets, row].sort((a, b) => a.page_index - b.page_index) };
  }
  const key = LIST_FOR[change.kind];
  if (!key) return next;
  const list = (state[key] as unknown as { id: string }[]) ?? [];
  const id = String(change.data.id);
  let out: { id: string }[];
  if (change.action === 'deleted') out = list.filter((r) => r.id !== id);
  else {
    const row = change.data as unknown as { id: string; version?: number };
    const cur = list.find((r) => r.id === id) as { version?: number } | undefined;
    if (cur && (cur.version ?? 0) > (row.version ?? 0)) return next; // stale echo
    out = cur ? list.map((r) => (r.id === id ? row : r)) : [...list, row];
  }
  return { ...next, [key]: out };
}

export function useTakeoff(opts: {
  documentId: string;
  api: ApiClient;
  sync: SyncClient | null;
  annotations: ReadonlyMap<string, AnnotationDTO>;
  enabled: boolean;
  user: { id: string; name: string } | null;
}) {
  const { documentId, api, sync, annotations, enabled } = opts;
  const client = useMemo(() => takeoffApi(api, documentId), [api, documentId]);
  const [state, setState] = useState<TakeoffStateDTO | null>(null);
  const [quantities, setQuantities] = useState<QuantitiesDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ui = useMemo(createUiStore, [documentId]);
  const view = useSyncExternalStore(ui.subscribe, ui.get);
  const stateRef = useRef(state);
  stateRef.current = state;
  const qTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);
  /** My own marks, newest last (takeoff-mode undo). */
  const myMarks = useRef<string[]>([]);

  const refreshQuantities = useCallback(
    (delay = 200) => {
      if (qTimer.current) clearTimeout(qTimer.current);
      qTimer.current = setTimeout(() => {
        qTimer.current = null;
        client
          .quantities()
          .then((q) => alive.current && setQuantities(q))
          .catch(() => undefined);
      }, delay);
    },
    [client],
  );

  const engines = useTakeoffEngines({ api, documentId, onPlaced: () => refreshQuantities(100) });

  const reload = useCallback(() => {
    client
      .state()
      .then((s) => {
        if (!alive.current) return;
        setState(s);
        setError(null);
      })
      .catch((e: Error) => alive.current && setError(e.message));
    refreshQuantities(0);
  }, [client, refreshQuantities]);

  useEffect(() => {
    alive.current = true;
    if (enabled) reload();
    return () => {
      alive.current = false;
      if (qTimer.current) clearTimeout(qTimer.current);
    };
  }, [enabled, reload]);

  // Live catalog changes and mark changes (anyone's) → patch + recompute totals.
  useEffect(() => {
    if (!sync || !enabled) return;
    const offFrame = sync.on('frame', ({ type, data }) => {
      if (type !== 'takeoff.changed') return;
      const change = data as TakeoffChange;
      const cur = stateRef.current;
      if (!cur) return;
      // Missed a change (e.g. reconnect): refetch rather than patch blindly.
      if (change.version && change.version > cur.version + 1) reload();
      else setState((s) => (s ? applyTakeoffChange(s, change) : s));
      refreshQuantities();
    });
    const offChange = sync.on('change', (changes) => {
      if (changes.some((c) => (c.dto && isMeasurement(c.dto)) || (c.previous && isMeasurement(c.previous)) || (c.kind === 'remove' && !c.dto))) refreshQuantities();
    });
    const offSynced = sync.on('synced', () => refreshQuantities());
    return () => {
      offFrame();
      offChange();
      offSynced();
    };
  }, [sync, enabled, reload, refreshQuantities]);

  const sequence = useMemo(() => markSequence(annotations.values() as Iterable<AnnotationDTO>), [annotations]);

  const items = state?.items ?? [];
  const assemblies = state?.assemblies ?? [];
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const assemblyById = useMemo(() => new Map(assemblies.map((a) => [a.id, a])), [assemblies]);

  const targetInfo = useCallback(
    (t: TakeoffTarget | null | undefined): { label: string; short: string; color: string; symbol: TakeoffItemDTO['symbol']; uom: string } | null => {
      if (!t) return null;
      if (t.kind === 'item') {
        const i = itemById.get(t.id);
        return i ? { label: `${i.code} ${i.description || i.name}`.trim(), short: i.code, color: i.color, symbol: i.symbol, uom: i.uom } : null;
      }
      const a = assemblyById.get(t.id);
      return a ? { label: a.name, short: a.code || abbreviationOf(a.name), color: a.color, symbol: a.symbol, uom: a.measure === 'count' ? 'EACH' : a.measure === 'linear' ? 'LF' : 'SF' } : null;
    },
    [itemById, assemblyById],
  );

  // ---- catalog writes (held-version rule; server wins on conflict) ---------------------
  const upsertLocal = (key: keyof TakeoffStateDTO, row: { id: string }) =>
    setState((s) => {
      if (!s) return s;
      const list = (s[key] as unknown as { id: string }[]).filter((r) => r.id !== row.id);
      return { ...s, [key]: [...list, row] };
    });

  const create = useCallback(
    async <T extends { id: string }>(key: 'items' | 'assemblies' | 'areas' | 'unit_types' | 'masks', data: Record<string, unknown>): Promise<T | null> => {
      try {
        const row = await client.create<T>(KIND_PATH[key], data);
        upsertLocal(key, row);
        refreshQuantities();
        return row;
      } catch (e) {
        toast(errorText(e), 'error');
        return null;
      }
    },
    [client, refreshQuantities],
  );

  const update = useCallback(
    async <T extends { id: string; version: number }>(key: 'items' | 'assemblies' | 'areas' | 'unit_types' | 'masks', row: T, patch: Record<string, unknown>): Promise<T | null> => {
      try {
        const out = await client.update<T>(KIND_PATH[key], row.id, row.version, patch);
        upsertLocal(key, out);
        refreshQuantities();
        return out;
      } catch (e) {
        if (e instanceof TakeoffConflict) {
          upsertLocal(key, e.server as unknown as { id: string });
          toast(e.message, 'warning');
          return null;
        }
        toast(errorText(e), 'error');
        return null;
      }
    },
    [client, refreshQuantities],
  );

  const remove = useCallback(
    async (key: 'items' | 'assemblies' | 'areas' | 'unit_types' | 'masks', id: string) => {
      try {
        await client.remove(KIND_PATH[key], id);
        setState((s) => (s ? { ...s, [key]: (s[key] as unknown as { id: string }[]).filter((r) => r.id !== id) } : s));
        refreshQuantities();
      } catch (e) {
        toast(errorText(e), 'error');
      }
    },
    [client, refreshQuantities],
  );

  const setSheet = useCallback(
    async (pageIndex: number, data: Record<string, unknown>) => {
      try {
        const cur = stateRef.current?.sheets.find((s) => s.page_index === pageIndex);
        const row = await client.setSheet(pageIndex, cur ? { ...data, version: cur.version } : data);
        setState((s) => (s ? applyTakeoffChange(s, { kind: 'sheet', action: 'updated', data: row as unknown as Record<string, unknown>, version: s.version }) : s));
        refreshQuantities();
        return row;
      } catch (e) {
        if (e instanceof TakeoffConflict) {
          setState((s) => (s ? applyTakeoffChange(s, { kind: 'sheet', action: 'updated', data: e.server, version: s.version }) : s));
          toast(e.message, 'warning');
          return null;
        }
        toast(errorText(e), 'error');
        return null;
      }
    },
    [client, refreshQuantities],
  );

  // ---- marks ---------------------------------------------------------------------------
  const linkFor = (t: TakeoffTarget): TakeoffLink => (t.kind === 'item' ? { item_id: t.id } : { assembly_id: t.id });

  const marksOf = useCallback(
    (t: TakeoffTarget, page?: number) =>
      [...annotations.values()].filter((a) => !a.deleted_at && isMeasurement(a) && targetOf(a)?.id === t.id && (page === undefined || a.page_index === page)),
    [annotations],
  );

  /** Click-to-count. Idempotent: a click on an existing mark of the same target is ignored (Alt forces). */
  const placeCount = useCallback(
    (page: number, p: PointDTO, force = false): AnnotationDTO | null => {
      const t = ui.get().target;
      if (!sync || !t) {
        if (!t) toast('Pick an item or host first (1–9 or the catalog)', 'info');
        return null;
      }
      const info = targetInfo(t);
      if (!force) {
        const near = marksOf(t, page).find((a) => a.type === 'count' && Math.hypot(a.bounds.x + a.bounds.width / 2 - p.x, a.bounds.y + a.bounds.height / 2 - p.y) <= MARK_SIZE / 2);
        if (near) {
          toast('Already counted here — Shift+click removes, Alt+click counts again', 'info');
          return null;
        }
      }
      const id = newId();
      const input = {
        id,
        type: 'count' as AnnotationDTO['type'],
        page_index: page,
        bounds: { x: p.x - MARK_SIZE / 2, y: p.y - MARK_SIZE / 2, width: MARK_SIZE, height: MARK_SIZE },
        color: info?.color ?? '#E53935',
        opacity: 1,
        stroke_width: null,
        takeoff: { ...linkFor(t), source: 'manual' },
      };
      myMarks.current.push(id);
      sync.create(input).catch((e: Error) => toast(e.message, 'error'));
      return sync.annotations.get(id) ?? null;
    },
    [sync, ui, targetInfo, marksOf],
  );

  const placeRun = useCallback(
    (kind: 'linear' | 'area', page: number, vertices: PointDTO[]) => {
      const t = ui.get().target;
      if (!sync || !t) {
        toast('Pick an item or host first', 'info');
        return;
      }
      const info = targetInfo(t);
      const xs = vertices.map((v) => v.x), ys = vertices.map((v) => v.y);
      const id = newId();
      myMarks.current.push(id);
      const runs = ui.get().runs;
      sync
        .create({
          id,
          type: kind as AnnotationDTO['type'],
          page_index: page,
          bounds: { x: Math.min(...xs), y: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs) || 1, height: Math.max(...ys) - Math.min(...ys) || 1 },
          vertices,
          color: info?.color ?? '#1E88E5',
          opacity: 1,
          stroke_width: 2,
          takeoff: { ...linkFor(t), ...(kind === 'linear' && runs > 1 ? { runs } : {}), source: 'manual' },
        })
        .catch((e: Error) => toast(e.message, 'error'));
    },
    [sync, ui, targetInfo],
  );

  const removeMark = useCallback(
    (id: string) => {
      sync?.delete(id).catch((e: Error) => toast(e.message, 'error'));
      if (ui.get().selectedMark === id) ui.set({ selectedMark: null });
    },
    [sync, ui],
  );

  const reassignMark = useCallback(
    (id: string, t: TakeoffTarget) => {
      const a = sync?.annotations.get(id);
      if (!a || !sync) return;
      const info = targetInfo(t);
      const link: TakeoffLink = { ...(a.takeoff ?? {}), ...linkFor(t) };
      if (t.kind === 'item') delete link.assembly_id;
      else delete link.item_id;
      sync.update(id, { takeoff: link, color: info?.color ?? a.color } as never).catch((e: Error) => toast(e.message, 'error'));
    },
    [sync, targetInfo],
  );

  /** Takeoff-mode undo: remove my most recent mark that still exists. */
  const undoLastMark = useCallback(() => {
    while (myMarks.current.length) {
      const id = myMarks.current.pop()!;
      const a = sync?.annotations.get(id);
      if (a && !a.deleted_at) {
        removeMark(id);
        return true;
      }
    }
    return false;
  }, [sync, removeMark]);

  // ---- tool / target -----------------------------------------------------------------------
  const setTool = useCallback((tool: TakeoffTool) => ui.set({ tool }), [ui]);
  const setTarget = useCallback((target: TakeoffTarget | null, tool?: TakeoffTool) => ui.set({ target, ...(tool ? { tool } : {}) }), [ui]);
  const favourite = useCallback(
    (slot: number): TakeoffTarget | null => {
      const it = items.find((i) => i.favorite_slot === slot);
      if (it) return { kind: 'item', id: it.id };
      const as = assemblies.find((a) => a.favorite_slot === slot);
      if (as) return { kind: 'assembly', id: as.id };
      // No explicit favourites: the Nth catalog entry (hosts first).
      const all: TakeoffTarget[] = [...assemblies.map((a) => ({ kind: 'assembly' as const, id: a.id })), ...items.map((i) => ({ kind: 'item' as const, id: i.id }))];
      const anyFav = items.some((i) => i.favorite_slot) || assemblies.some((a) => a.favorite_slot);
      return anyFav ? null : all[slot - 1] ?? null;
    },
    [items, assemblies],
  );

  // Our activity next to our cursor on collaborators' screens.
  useEffect(() => {
    if (!sync) return;
    if (!enabled) {
      sync.setCursorLabel('');
      return;
    }
    const info = targetInfo(view.target);
    const toolWord: Record<TakeoffTool, string> = { select: 'Select', hand: 'Pan', count: 'Q', linear: 'W', area: 'A', calibrate: 'Calibrate', zone: 'Area outline', unit: 'Unit region', mask: 'Mask', autocount: 'Auto-count', schedule: 'Import table' };
    const label = ['count', 'linear', 'area'].includes(view.tool) && info ? `${toolWord[view.tool]} ${info.label}` : toolWord[view.tool];
    sync.setCursorLabel(label);
    return () => sync.setCursorLabel('');
  }, [sync, enabled, view.tool, view.target, targetInfo]);

  return {
    enabled,
    engines,
    client,
    state,
    quantities,
    error,
    ui: view,
    uiStore: ui,
    sequence,
    items,
    assemblies,
    itemById,
    assemblyById,
    targetInfo,
    reload,
    refreshQuantities,
    create,
    update,
    remove,
    setSheet,
    placeCount,
    placeRun,
    removeMark,
    reassignMark,
    undoLastMark,
    marksOf,
    setTool,
    setTarget,
    favourite,
    sheetFor: (page: number) => state?.sheets.find((s) => s.page_index === page) ?? null,
  };
}

function abbreviationOf(name: string): string {
  return (name.replace(/-/g, ' ').split(/\s+/).filter(Boolean).slice(0, 4).map((w) => w[0]).join('') || 'H').toUpperCase();
}

function errorText(e: unknown): string {
  const err = e as { message?: string; body?: unknown };
  if (err.body && typeof err.body === 'object') {
    const first = Object.values(err.body as Record<string, unknown>)[0];
    if (Array.isArray(first) && first.length) return String(first[0]);
    if (typeof first === 'string') return first;
  }
  return err.message ?? 'Request failed';
}

export type { TakeoffAssemblyDTO, TakeoffItemDTO };
